import axios from "axios";

const SERVER_URL = `https://auth.umlang.com/auth/api/v1/auth`;

// تابع برای بررسی اعتبار توکن
export const isTokenExpired = (token) => {
  try {
    // دیکد کردن توکن
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64));

    // بررسی تاریخ انقضا
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch (error) {
    console.error("خطا در بررسی توکن:", error);
    return true; // اگر خطا داشت، توکن رو منقضی در نظر بگیر
  }
};

// تابع برای دریافت توکن جدید
export const getNewToken = async () => {
  try {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      return {
        success: false,
        error: "رفرش توکن یافت نشد",
        shouldLogout: true,
      };
    }

    const response = await axios.post(`${SERVER_URL}/login/getNewTokenCode`, {
      newTokenRequest: refreshToken,
      device: "newWeb",
    });

    if (response.data?.success && response.data?.token) {
      // ذخیره توکن جدید
      localStorage.setItem("token", response.data.token);

      // اگه رفرش توکن جدید هم برگشت، ذخیره کن
      if (response.data.refreshToken) {
        localStorage.setItem("refreshToken", response.data.refreshToken);
      }

      return {
        success: true,
        token: response.data.token,
      };
    } else {
      return {
        success: false,
        error: response.data?.message || "خطا در دریافت توکن جدید",
        shouldLogout: true,
      };
    }
  } catch (error) {
    console.error("خطا در دریافت توکن جدید:", error);

    let errorMessage = "خطا در دریافت توکن جدید";
    let shouldLogout = false;

    if (error.response) {
      if (
        error.response.data?.message?.includes("No matching document found")
      ) {
        errorMessage = "رفرش توکن نامعتبر است. لطفاً مجدداً وارد شوید.";
        shouldLogout = true;
      } else if (error.response.status === 401) {
        errorMessage = "احراز هویت ناموفق. لطفاً مجدداً وارد شوید.";
        shouldLogout = true;
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    }

    return {
      success: false,
      error: errorMessage,
      shouldLogout,
    };
  }
};

// تابع اصلی برای اطمینان از اعتبار توکن (قبل از درخواست)
export const ensureValidToken = async () => {
  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  // اگه توکنی وجود نداره
  if (!token) {
    return {
      valid: false,
      needLogin: true,
      message: "لطفا وارد حساب خود شوید",
    };
  }

  // اگه توکن منقضی شده
  if (isTokenExpired(token)) {
    // اگه رفرش توکن وجود نداره
    if (!refreshToken) {
      logoutUser("رفرش توکن یافت نشد");
      return {
        valid: false,
        needLogin: true,
        message: "لطفا مجدداً وارد شوید",
      };
    }

    const refreshResult = await getNewToken();

    if (refreshResult.success) {
      return {
        valid: true,
        token: refreshResult.token,
        refreshed: true,
      };
    } else {
      if (refreshResult.shouldLogout) {
        logoutUser(refreshResult.error);
      }

      return {
        valid: false,
        needLogin: refreshResult.shouldLogout,
        message: refreshResult.error,
        shouldLogout: refreshResult.shouldLogout,
      };
    }
  }

  // توکن معتبره
  return {
    valid: true,
    token: token,
  };
};

// تابع برای خروج کاربر
export const logoutUser = (reason = "نشست منقضی شده") => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userData");
};

// تابع برای لاگین
export const loginUser = (token, refreshToken, userData = null) => {
  if (token) localStorage.setItem("token", token);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
  if (userData) localStorage.setItem("userData", JSON.stringify(userData));
};

// تابع کمکی برای درخواست‌های نیازمند توکن
export const makeRequest = async (apiFunction, ...args) => {
  try {
    // بررسی توکن قبل از درخواست
    const tokenStatus = await ensureValidToken();

    if (!tokenStatus.valid) {
      if (tokenStatus.needLogin) {
        // اگه نیاز به لاگین مجدد داره
        logoutUser(tokenStatus.message);
        window.location.href = "/";
        throw new Error(tokenStatus.message);
      } else {
        throw new Error(tokenStatus.message || "خطا در احراز هویت");
      }
    }

    // اجرای درخواست اصلی
    const response = await apiFunction(...args);
    return response;
  } catch (error) {
    // اگه خطای 401 باشه (بعد از درخواست)
    if (error.response?.status === 401) {
      const errorMessage = error.response.data?.message || "";

      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("token expired")
      ) {
        const refreshResult = await getNewToken();

        if (refreshResult.success) {
          // تکرار درخواست با توکن جدید
          return await apiFunction(...args);
        } else {
          if (refreshResult.shouldLogout) {
            logoutUser("عدم امکان دریافت توکن جدید");
            window.location.href = "/";
          }
        }
      } else if (
        errorMessage.includes("jwt malformed") ||
        errorMessage.includes("invalid signature")
      ) {
        logoutUser("توکن نامعتبر");
        window.location.href = "/";
      }
    } else if (error.response?.status === 500) {
      const errorMessage = error.response.data?.message || "";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("token expired")
      ) {
        const refreshResult = await getNewToken();

        if (refreshResult.success) {
          // تکرار درخواست با توکن جدید
          return await apiFunction(...args);
        } else {
          if (refreshResult.shouldLogout) {
            logoutUser("عدم امکان دریافت توکن جدید");
            window.location.href = "/";
          }
        }
      } else if (
        errorMessage.includes("jwt malformed") ||
        errorMessage.includes("invalid signature")
      ) {
        logoutUser("توکن نامعتبر");
        window.location.href = "/";
      }
    }

    throw error;
  }
};

// utils/handleJWT.js
// (این تابع رو به انتهای فایل handleJWT.js اضافه کن)

/**
 * بررسی کاربر ویژه بودن از روی توکن
 */
export const checkUserPremium = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return false;

    // دیکد کردن توکن
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64));

    // بررسی وجود userPremium در type
    return (
      payload.type &&
      Array.isArray(payload.type) &&
      payload.type.includes("userPremium")
    );
  } catch (error) {
    console.error("خطا در دیکد کردن توکن:", error);
    return false;
  }
};
